/**
 * Gráfico horizontal de barras — Top N variaciones absolutas.
 * Verde = variación positiva favorable, Rojo = variación negativa desfavorable.
 * SVG puro, sin librerías externas.
 */
import { TopVariationItemDto } from '../../../../../shared/services/results/types.comparativos';
import { money } from './comparativosUtils';

interface Props {
  items: TopVariationItemDto[];
  label1: string;
  label2: string;
}

export function TopVariationsChart({ items, label1, label2 }: Props) {
  if (items.length === 0) return null;

  const BAR_H = 20;
  const ROW_H = BAR_H + 14; // barra + etiqueta
  const PAD   = { top: 20, right: 140, bottom: 24, left: 240 };
  const W     = 720;
  const chartH = items.length * ROW_H;
  const H      = chartH + PAD.top + PAD.bottom;
  const chartW = W - PAD.left - PAD.right;

  const maxVal = Math.max(...items.map((it) => Math.abs(it.variacionAbsoluta)), 1);
  const bw = (v: number) => (Math.abs(v) / maxVal) * chartW;

  // Color favorable: INGRESO aumenta o GASTOS disminuye → verde
  const favorable = (it: TopVariationItemDto) =>
    (it.tipoCuenta === 'INGRESO' && it.variacionAbsoluta > 0) ||
    (it.tipoCuenta === 'GASTOS' && it.variacionAbsoluta < 0);

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-ink-700">
        Top {items.length} variaciones — {label1} → {label2}
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-label="Top variaciones">
        {/* Eje vertical */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#cbd5e1" strokeWidth={1} />

        {items.map((it, i) => {
          const y0  = PAD.top + i * ROW_H;
          const fill = favorable(it) ? '#10b981' : '#ef4444';
          const w = bw(it.variacionAbsoluta);
          const tipoBadge = it.tipoCuenta === 'INGRESO' ? 'ING' : 'GTO';
          const tipoBadgeColor = it.tipoCuenta === 'INGRESO' ? '#059669' : '#dc2626';

          return (
            <g key={`${it.tipoCuenta}::${it.cuentaCodigo}`}>
              {/* Badge tipo */}
              <rect x={PAD.left - 232} y={y0 + 4} width={26} height={BAR_H - 8} fill={tipoBadgeColor} rx={2} opacity={0.15} />
              <text x={PAD.left - 219} y={y0 + BAR_H - 6} textAnchor="middle" fontSize={8} fontWeight={700} fill={tipoBadgeColor}>{tipoBadge}</text>

              {/* Nombre cuenta */}
              <text
                x={PAD.left - 8}
                y={y0 + BAR_H - 5}
                textAnchor="end"
                fontSize={9}
                fill="#475569"
              >
                {it.cuentaDescripcion.length > 30
                  ? it.cuentaDescripcion.slice(0, 28) + '…'
                  : it.cuentaDescripcion}
              </text>

              {/* Barra */}
              <rect
                x={PAD.left}
                y={y0 + 2}
                width={w}
                height={BAR_H - 4}
                fill={fill}
                rx={3}
                opacity={0.9}
              >
                <title>
                  {it.cuentaDescripcion}: {label1} {money(it.valor1)} → {label2} {money(it.valor2)} ({it.variacionAbsoluta >= 0 ? '+' : ''}{money(it.variacionAbsoluta)})
                </title>
              </rect>

              {/* Valor al final */}
              <text
                x={PAD.left + w + 5}
                y={y0 + BAR_H - 5}
                fontSize={9}
                fill={favorable(it) ? '#065f46' : '#991b1b'}
                fontWeight={600}
              >
                {it.variacionAbsoluta >= 0 ? '+' : ''}{money(it.variacionAbsoluta)}
              </text>

              {/* Separador */}
              {i < items.length - 1 && (
                <line x1={0} y1={y0 + ROW_H - 1} x2={W} y2={y0 + ROW_H - 1} stroke="#f1f5f9" strokeWidth={1} />
              )}
            </g>
          );
        })}

        {/* Leyenda */}
        <rect x={PAD.left} y={H - 14} width={10} height={8} fill="#10b981" rx={1} />
        <text x={PAD.left + 14} y={H - 7} fontSize={9} fill="#64748b">Variación favorable</text>
        <rect x={PAD.left + 120} y={H - 14} width={10} height={8} fill="#ef4444" rx={1} />
        <text x={PAD.left + 134} y={H - 7} fontSize={9} fill="#64748b">Variación desfavorable</text>
      </svg>
    </div>
  );
}
